import React, { useEffect, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Button, TextField, Box, Typography, Select, MenuItem, InputLabel, FormControl, Chip } from '@mui/material';
import { createServer, updateServer, getServer } from '../../services/serverService';
import { getHosts } from '../../services/hostService';
import { useNavigate, useParams } from 'react-router-dom';

const ServerForm = ({ editMode }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [hosts, setHosts] = useState([]);
  const [tags, setTags] = useState([]);
  const [tagInput, setTagInput] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const hostsData = await getHosts();
        setHosts(hostsData);

        if (editMode && id) {
          const serverData = await getServer(id);
          setTags(serverData.tags || []);
          return {
            name: serverData.name,
            ip: serverData.ip,
            host: serverData.host._id
          };
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };

    fetchData();
  }, [editMode, id]);

  const initialValues = {
    name: '',
    ip: '',
    host: ''
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    ip: Yup.string()
      .required('IP address is required')
      .matches(
        /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/,
        'Invalid IP address'
      ),
    host: Yup.string().required('Host is required')
  });

  const handleAddTag = () => {
    if (tagInput && !tags.includes(tagInput)) {
      setTags([...tags, tagInput]);
      setTagInput('');
    }
  };

  const handleDeleteTag = (tagToDelete) => {
    setTags(tags.filter((tag) => tag !== tagToDelete));
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const serverData = {
        ...values,
        tags
      };

      if (editMode) {
        await updateServer(id, serverData);
      } else {
        await createServer(serverData);
      }
      navigate('/servers');
    } catch (error) {
      console.error('Error saving server:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto' }}>
      <Typography variant="h5" component="h2" gutterBottom>
        {editMode ? 'Edit Server' : 'Add Server'}
      </Typography>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, values, handleChange }) => (
          <Form>
            <Field
              as={TextField}
              name="name"
              label="Name"
              fullWidth
              margin="normal"
              variant="outlined"
            />
            <ErrorMessage name="name" component="div" />

            <Field
              as={TextField}
              name="ip"
              label="IP Address"
              fullWidth
              margin="normal"
              variant="outlined"
            />
            <ErrorMessage name="ip" component="div" />

            <FormControl fullWidth margin="normal">
              <InputLabel>Host</InputLabel>
              <Field
                as={Select}
                name="host"
                label="Host"
                value={values.host}
                onChange={handleChange}
              >
                {hosts.map((host) => (
                  <MenuItem key={host._id} value={host._id}>
                    {host.name}
                  </MenuItem>
                ))}
              </Field>
              <ErrorMessage name="host" component="div" />
            </FormControl>

            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Tags
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TextField
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  label="Add tag"
                  size="small"
                  sx={{ mr: 1 }}
                />
                <Button
                  variant="outlined"
                  onClick={handleAddTag}
                  disabled={!tagInput}
                >
                  Add
                </Button>
              </Box>
              <Box>
                {tags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    onDelete={() => handleDeleteTag(tag)}
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
              </Box>
            </Box>

            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              sx={{ mt: 2 }}
            >
              Save
            </Button>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default ServerForm;